import React, { useCallback, useEffect, useState } from 'react';
import { Card, Button, Input, Select } from '../../components/ui/Kit';
import Modal from '../../components/ui/Modal';
import Spinner from '../../components/ui/Spinner';
import SafeIcon from '../../common/SafeIcon';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../contexts/AuthContext';
import * as FiIcons from 'react-icons/fi';
import { api } from '../../lib/api';
import { CHANNEL_LABELS, CHANNEL_VALUES, LEAD_TYPE_FILTER_OPTIONS } from '../../lib/leadMeta';

const { FiPlus, FiEdit2, FiTrash2, FiCopy, FiZap, FiLink } = FiIcons;

const CHANNEL_OPTIONS = [
  { value: '', label: '— None —' },
  ...CHANNEL_VALUES.map((v) => ({ value: v, label: `${CHANNEL_LABELS[v]} (${v})` })),
];

const LEAD_TYPE_OPTIONS = LEAD_TYPE_FILTER_OPTIONS;

function ToggleSwitch({ checked, onChange, disabled, id }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      id={id}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? 'bg-primary' : 'bg-gray-200'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

const emptyAgentForm = () => ({
  name: '',
  thoughtly_agent_id: '',
  channel: '',
  lead_type: '',
  description: '',
  is_active: true,
});

function AgentFormModal({ isOpen, onClose, initial, agencyId, onSaved }) {
  const [form, setForm] = useState(emptyAgentForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    if (initial) {
      setForm({
        name: initial.name ?? '',
        thoughtly_agent_id: initial.thoughtly_agent_id ?? '',
        channel: initial.channel ?? '',
        lead_type: initial.lead_type ?? '',
        description: initial.description ?? '',
        is_active: initial.is_active !== false,
      });
    } else {
      setForm(emptyAgentForm());
    }
  }, [isOpen, initial]);

  const set = (field) => (e) => {
    const v = e?.target?.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: v }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.thoughtly_agent_id.trim()) {
      setError('Name and Thoughtly Agent ID are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        agency_id: agencyId,
        name: form.name.trim(),
        thoughtly_agent_id: form.thoughtly_agent_id.trim(),
        channel: form.channel || null,
        lead_type: form.lead_type || null,
        description: form.description.trim() || null,
        is_active: form.is_active,
      };
      if (initial?.id) {
        await api.thoughtly.updateAgent(initial.id, payload);
      } else {
        await api.thoughtly.createAgent(payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.message || 'Could not save agent');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !saving && onClose()}
      title={initial?.id ? 'Edit AI Agent' : 'Add AI Agent'}
      className="max-w-lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4 mt-2">
        <Input
          label="Name"
          value={form.name}
          onChange={set('name')}
          placeholder="Aged Leads Agent"
          disabled={saving}
          required
        />
        <Input
          label="Thoughtly Agent ID"
          value={form.thoughtly_agent_id}
          onChange={set('thoughtly_agent_id')}
          placeholder="From Thoughtly dashboard"
          disabled={saving}
          required
        />
        <Select
          label="Channel"
          value={form.channel}
          onChange={set('channel')}
          options={CHANNEL_OPTIONS}
          disabled={saving}
        />
        <Select
          label="Lead type (optional)"
          value={form.lead_type}
          onChange={set('lead_type')}
          options={LEAD_TYPE_OPTIONS}
          disabled={saving}
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
          <textarea
            className="w-full border border-gray-200 rounded-md p-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none min-h-[72px] resize-y"
            value={form.description}
            onChange={set('description')}
            disabled={saving}
            placeholder="Optional notes for your team"
          />
        </div>
        <div className="flex items-center gap-3">
          <ToggleSwitch
            id="agent-active"
            checked={form.is_active}
            onChange={(v) => setForm((prev) => ({ ...prev, is_active: v }))}
            disabled={saving}
          />
          <label htmlFor="agent-active" className="text-sm text-gray-700">
            Active
          </label>
        </div>
        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" size="sm" variant="primary" disabled={saving}>
            {saving ? (
              <>
                <Spinner className="mr-2" />
                Saving…
              </>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export default function AiCallerTab() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const agencyId = user?.agency_id;

  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [webhookConfig, setWebhookConfig] = useState(null);
  const [webhookLoading, setWebhookLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const loadAgents = useCallback(async () => {
    if (!agencyId) return;
    setLoading(true);
    try {
      const rows = await api.thoughtly.listAgents(agencyId);
      setAgents(rows);
    } catch (err) {
      addToast({ type: 'error', title: 'Could not load AI agents', message: err.message });
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, [agencyId, addToast]);

  const loadWebhookConfig = useCallback(async () => {
    setWebhookLoading(true);
    try {
      const cfg = await api.thoughtly.getWebhookConfig();
      setWebhookConfig(cfg);
    } catch {
      setWebhookConfig({
        webhook_base_url: 'https://ogxgxzvxbmnszfxbiuai.supabase.co/functions/v1/thoughtly-webhook',
        secret_suffix: null,
      });
    } finally {
      setWebhookLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAgents();
    void loadWebhookConfig();
  }, [loadAgents, loadWebhookConfig]);

  const copyWebhookUrl = async () => {
    const url = webhookConfig?.webhook_base_url;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      addToast({ type: 'success', title: 'Webhook URL copied' });
    } catch {
      addToast({ type: 'error', title: 'Could not copy to clipboard' });
    }
  };

  const handleToggleActive = async (row) => {
    setTogglingId(row.id);
    try {
      await api.thoughtly.updateAgent(row.id, { is_active: !row.is_active });
      await loadAgents();
    } catch (err) {
      addToast({ type: 'error', title: 'Update failed', message: err.message });
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.thoughtly.deleteAgent(deleteTarget.id);
      addToast({ type: 'success', title: 'AI agent deleted' });
      setDeleteTarget(null);
      await loadAgents();
    } catch (err) {
      addToast({ type: 'error', title: 'Delete failed', message: err.message });
    } finally {
      setDeleting(false);
    }
  };

  const channelLabel = (ch) => (ch ? CHANNEL_LABELS[ch] || ch : '—');
  const leadTypeLabel = (lt) =>
    LEAD_TYPE_OPTIONS.find((o) => o.value === lt)?.label || lt || '—';

  return (
    <div className="space-y-8">
      <Card className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <SafeIcon icon={FiZap} className="text-primary w-5 h-5" />
              Configured AI Agents
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Map Thoughtly agents to channels and lead types for auto-selection on leads.
            </p>
          </div>
          <Button
            size="sm"
            variant="primary"
            onClick={() => {
              setEditingAgent(null);
              setFormOpen(true);
            }}
          >
            <SafeIcon icon={FiPlus} className="mr-2" />
            Add AI Agent
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 py-10 text-sm text-gray-500 justify-center">
            <Spinner className="text-primary" />
            Loading agents…
          </div>
        ) : agents.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-gray-200 rounded-lg bg-gray-50/50">
            <SafeIcon icon={FiZap} className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-700">
              No AI agents configured yet — add your first agent to get started
            </p>
            <Button
              size="sm"
              className="mt-4"
              variant="primary"
              onClick={() => {
                setEditingAgent(null);
                setFormOpen(true);
              }}
            >
              <SafeIcon icon={FiPlus} className="mr-2" />
              Add AI Agent
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto border border-gray-100 rounded-lg">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3 font-medium text-gray-500">Name</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Channel</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Lead Type</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Active</th>
                  <th className="px-4 py-3 font-medium text-gray-500 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {agents.map((row) => (
                  <tr key={row.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="px-4 py-3 font-medium text-gray-900">{row.name}</td>
                    <td className="px-4 py-3 text-gray-600">{channelLabel(row.channel)}</td>
                    <td className="px-4 py-3 text-gray-600">{leadTypeLabel(row.lead_type)}</td>
                    <td className="px-4 py-3">
                      <ToggleSwitch
                        checked={row.is_active !== false}
                        onChange={() => void handleToggleActive(row)}
                        disabled={togglingId === row.id}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          type="button"
                          className="p-2 rounded-md text-gray-500 hover:text-primary hover:bg-gray-100"
                          title="Edit"
                          onClick={() => {
                            setEditingAgent(row);
                            setFormOpen(true);
                          }}
                        >
                          <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          className="p-2 rounded-md text-gray-500 hover:text-red-600 hover:bg-red-50"
                          title="Delete"
                          onClick={() => setDeleteTarget(row)}
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-2">
          <SafeIcon icon={FiLink} className="text-primary w-5 h-5" />
          Webhook Configuration
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Paste this URL into your Thoughtly agent&apos;s webhook settings. Append your secret as a
          query parameter: <code className="text-xs bg-gray-100 px-1 rounded">?secret=YOUR_SECRET</code>
        </p>
        {webhookLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
            <Spinner className="text-primary" />
            Loading…
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <code className="flex-1 min-w-0 text-xs sm:text-sm bg-gray-50 border border-gray-200 rounded-md px-3 py-2.5 break-all text-gray-800">
                {webhookConfig?.webhook_base_url ?? '—'}
              </code>
              <Button type="button" variant="outline" size="sm" onClick={() => void copyWebhookUrl()}>
                <SafeIcon icon={FiCopy} className="mr-2 w-4 h-4" />
                Copy
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              Set the webhook secret to the value stored in Supabase as{' '}
              <span className="font-mono text-xs bg-gray-100 px-1 rounded">THOUGHTLY_WEBHOOK_SECRET</span>.
              Thoughtly does not support custom headers; use the{' '}
              <span className="font-mono text-xs">secret</span> query parameter on the URL above.
            </p>
            {webhookConfig?.secret_suffix ? (
              <p className="text-sm text-gray-700 mt-2 font-medium">
                Secret ending in: <span className="font-mono tracking-widest">{webhookConfig.secret_suffix}</span>
              </p>
            ) : (
              <p className="text-sm text-amber-700 mt-2 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
                Webhook secret is not configured in Supabase yet.
              </p>
            )}
          </>
        )}
      </Card>

      <AgentFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        initial={editingAgent}
        agencyId={agencyId}
        onSaved={() => {
          addToast({ type: 'success', title: editingAgent ? 'Agent updated' : 'Agent created' });
          void loadAgents();
        }}
      />

      <Modal
        isOpen={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        title="Delete AI agent?"
        className="max-w-md"
      >
        <p className="text-sm text-gray-600 mt-2">
          Remove <span className="font-medium text-gray-900">{deleteTarget?.name}</span>? This
          cannot be undone.
        </p>
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
          <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button size="sm" variant="danger" onClick={() => void handleDelete()} disabled={deleting}>
            {deleting ? <Spinner className="mr-2" /> : null}
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}
